// PPG-style wavetable bank generator.
//
// D7: we do NOT embed proprietary PPG ROM data. Instead we *generate* a bank of
// single-cycle waveforms that morph through the classic progression — sine ->
// triangle -> saw -> square -> narrow pulse -> formant/vocal -> buzz — which is
// the character the "wavetable scan" knob sweeps through.
//
// Each waveform is built by additive synthesis from a harmonic-amplitude
// spectrum, then peak-normalized. Pure data + math so it is unit-testable; the
// resulting bank is passed to the wavetable worklet via processorOptions.

export interface WavetableBank {
  /** Samples per single-cycle waveform. */
  tableSize: number;
  /** Number of waveforms in the bank. */
  count: number;
  /** Flat PCM, length = count * tableSize, waveform k at [k*tableSize ..]. */
  samples: Float32Array;
}

export const DEFAULT_TABLE_SIZE = 2048;
export const DEFAULT_BANK_COUNT = 64;
const NUM_HARMONICS = 64; // band-limited generation (some aliasing above ~C6)

type Spectrum = Float32Array; // index h = harmonic number, value = amplitude

function spectrum(fn: (h: number) => number): Spectrum {
  const a = new Float32Array(NUM_HARMONICS + 1);
  for (let h = 1; h <= NUM_HARMONICS; h++) a[h] = fn(h);
  return a;
}

// Anchor spectra the bank morphs between.
const ANCHORS: Spectrum[] = [
  spectrum((h) => (h === 1 ? 1 : 0)), // sine
  spectrum((h) => (h % 2 === 1 ? 1 / (h * h) : 0)), // triangle
  spectrum((h) => 1 / h), // sawtooth
  spectrum((h) => (h % 2 === 1 ? 1 / h : 0)), // square
  spectrum((h) => Math.abs(Math.sin(Math.PI * h * 0.25)) / h), // ~25% pulse
  spectrum((h) => {
    // formant/vocal: two resonant peaks
    const p1 = Math.exp(-((h - 4) ** 2) / 6);
    const p2 = Math.exp(-((h - 11) ** 2) / 14);
    return (p1 + 0.6 * p2) / h ** 0.25;
  }),
  spectrum((h) => 1 / Math.sqrt(h)), // buzz (bright)
];

function lerpSpectra(a: Spectrum, b: Spectrum, t: number): Spectrum {
  const out = new Float32Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i]! + (b[i]! - a[i]!) * t;
  return out;
}

/** Soft roll-off of the top harmonics to tame aliasing a little. */
function topRolloff(h: number): number {
  const x = h / NUM_HARMONICS;
  return 0.5 * (1 + Math.cos(Math.PI * Math.min(1, x) ** 4));
}

function synthesize(spec: Spectrum, tableSize: number): Float32Array {
  const wave = new Float32Array(tableSize);
  for (let h = 1; h <= NUM_HARMONICS; h++) {
    const amp = spec[h]! * topRolloff(h);
    if (amp === 0) continue;
    const w = (2 * Math.PI * h) / tableSize;
    for (let n = 0; n < tableSize; n++) wave[n]! += amp * Math.sin(w * n);
  }
  // Peak-normalize.
  let peak = 0;
  for (let n = 0; n < tableSize; n++) peak = Math.max(peak, Math.abs(wave[n]!));
  if (peak > 0) {
    const g = 1 / peak;
    for (let n = 0; n < tableSize; n++) wave[n]! *= g;
  }
  return wave;
}

export function generateWavetableBank(
  count = DEFAULT_BANK_COUNT,
  tableSize = DEFAULT_TABLE_SIZE,
): WavetableBank {
  const samples = new Float32Array(count * tableSize);
  const segments = ANCHORS.length - 1;
  for (let k = 0; k < count; k++) {
    const u = count > 1 ? k / (count - 1) : 0;
    const pos = u * segments;
    const i0 = Math.min(segments - 1, Math.floor(pos));
    const frac = pos - i0;
    const spec = lerpSpectra(ANCHORS[i0]!, ANCHORS[i0 + 1]!, frac);
    samples.set(synthesize(spec, tableSize), k * tableSize);
  }
  return { tableSize, count, samples };
}
